import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { shootCoins, playCoins, playThud, playBellDing, playWhistle } from "@/lib/confetti";
import CoinStreakBanner from "@/components/CoinStreakBanner";

function animateNumber(setter, from, to, duration) {
  const start = performance.now();
  const step = (now) => {
    const p = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - p, 3);
    setter(Math.round(from + (to - from) * eased));
    if (p < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

// Alternate boy/girl every appearance (persisted across sessions).
function nextGender() {
  const n = parseInt(localStorage.getItem("hsGenderN") || "0", 10);
  localStorage.setItem("hsGenderN", String(n + 1));
  return n % 2 === 0 ? "boy" : "girl";
}

// The chunky carnival slugger, side profile facing right. Bright colours,
// backwards cap (girl gets a ponytail through the cap hole). Deliberately
// NOT Mario: teal/orange or magenta/yellow, no moustache, no red cap.
function Slugger({ gender }) {
  const c = gender === "girl"
    ? { overall: "#FF3DB5", overallDark: "#D1187F", shirt: "#FFD600", cap: "#00E5FF", capDark: "#00B8D4", skin: "#FFCB9E" }
    : { overall: "#00B8A9", overallDark: "#00897B", shirt: "#FF8C00", cap: "#7C4DFF", capDark: "#5E35B1", skin: "#FFCB9E" };
  return (
    <svg width="92" height="112" viewBox="0 0 92 112" fill="none">
      {/* legs + boots */}
      <rect x="30" y="78" width="13" height="26" rx="5" fill={c.overallDark} />
      <rect x="49" y="78" width="13" height="26" rx="5" fill={c.overallDark} />
      <path d="M28 100 h18 a4 4 0 0 1 4 4 v4 h-24 v-5 a3 3 0 0 1 2 -3z" fill="#3E2723" />
      <path d="M47 100 h18 a4 4 0 0 1 4 4 v4 h-24 v-5 a3 3 0 0 1 2 -3z" fill="#3E2723" />
      {/* big round body in coveralls */}
      <ellipse cx="46" cy="63" rx="28" ry="25" fill={c.overall} />
      {/* belly patch + pocket */}
      <ellipse cx="50" cy="68" rx="16" ry="13" fill={c.overallDark} opacity="0.35" />
      <rect x="42" y="52" width="16" height="11" rx="3" fill={c.overallDark} />
      {/* shirt shoulders + rear arm */}
      <path d="M22 52 a26 26 0 0 1 22 -13 l4 9 a30 30 0 0 0 -20 10z" fill={c.shirt} />
      <ellipse cx="26" cy="58" rx="7" ry="10" fill={c.shirt} transform="rotate(18 26 58)" />
      {/* front arm reaching to hammer grip */}
      <ellipse cx="63" cy="53" rx="7" ry="12" fill={c.shirt} transform="rotate(-38 63 53)" />
      <circle cx="70" cy="45" r="5" fill={c.skin} />
      {/* straps */}
      <rect x="36" y="40" width="7" height="16" rx="3" fill={c.overallDark} transform="rotate(8 36 40)" />
      <rect x="54" y="38" width="7" height="16" rx="3" fill={c.overallDark} transform="rotate(-8 54 38)" />
      {/* head */}
      <circle cx="52" cy="24" r="15" fill={c.skin} />
      <circle cx="59" cy="22" r="2" fill="#26221E" />
      {gender === "girl" && <path d="M56 17 q4 -1 6 2" stroke="#26221E" strokeWidth="1.6" fill="none" strokeLinecap="round" />}
      <path d="M58 30 q4 2 7 -1" stroke="#26221E" strokeWidth="2" fill="none" strokeLinecap="round" />
      <circle cx="46" cy="29" r="3.4" fill="#FF8A80" opacity="0.65" />
      {/* backwards cap: dome + brim pointing back (left), snap band at front */}
      <path d="M38 20 a15 15 0 0 1 28 -5 l-28 7z" fill={c.cap} />
      <rect x="27" y="15" width="13" height="6" rx="3" fill={c.capDark} />
      <rect x="60" y="12" width="8" height="5" rx="2.5" fill={c.capDark} />
      {/* girl: ponytail through the cap hole at the front */}
      {gender === "girl" && (
        <path d="M66 13 q10 -2 9 8 q-1 8 -8 11 q4 -7 1 -11 q-2 -4 -2 -8z" fill="#6D4C41" />
      )}
    </svg>
  );
}

// Oversized wooden mallet. transform-origin is set where the hands grip it.
function Mallet() {
  return (
    <svg width="86" height="96" viewBox="0 0 86 96" fill="none">
      <rect x="39" y="22" width="8" height="70" rx="4" fill="#8D6E63" />
      <rect x="14" y="2" width="58" height="26" rx="9" fill="#D9A05B" />
      <rect x="20" y="2" width="6" height="26" fill="#8a5a2b" opacity="0.7" />
      <rect x="60" y="2" width="6" height="26" fill="#8a5a2b" opacity="0.7" />
    </svg>
  );
}

// Carnival high-striker celebration: the slugger walks in dragging the mallet,
// takes an exaggerated swing, the puck rockets up, the bell DINGS -> coins shoot,
// then they shoulder the mallet and whistle their way off. Next appears once
// they're half off screen.
export default function HighStrikerCelebration({
  coinsAwarded, totalCoins, scorePercent, correctCount, totalCount,
  canAdvance, onRetry, onContinue, soundOn = true, hasPrev = false, onPrev, onDone,
}) {
  const prevTotal = Math.max(0, (totalCoins || 0) - (coinsAwarded || 0));
  const [phase, setPhase] = useState("walkIn"); // walkIn -> windup -> slam -> ring -> walkOff -> done
  const [count, setCount] = useState(0);
  const [total, setTotal] = useState(prevTotal);
  const [gender] = useState(nextGender);
  const timers = useRef([]);
  const rootRef = useRef(null);
  const doneRef = useRef(null);
  const rang = phase === "ring" || phase === "walkOff" || phase === "done";

  useEffect(() => {
    const add = (fn, ms) => timers.current.push(setTimeout(fn, ms));
    add(() => setPhase("windup"), 2400);
    add(() => setPhase("slam"), 3300);
    add(() => { if (soundOn) playThud(); }, 3480);
    add(() => {
      setPhase("ring");
      if (soundOn) { playBellDing(7, 0.5); shootCoins(); playCoins(11); }
      animateNumber(setCount, 0, coinsAwarded, 1100);
      animateNumber(setTotal, prevTotal, totalCoins, 1300);
    }, 4150);
    add(() => {
      setPhase("walkOff");
      if (soundOn) playWhistle();
    }, 5600);
    add(() => {
      setPhase("done"); // slugger is ~half off screen now
      onDone && onDone();
    }, 6900);
    return () => timers.current.forEach(clearTimeout);
    // eslint-disable-next-line
  }, []);

  // Character x-position across phases (relative to its parked strike spot).
  const charAnim =
    phase === "walkIn" ? { x: 0, transition: { duration: 2.3, ease: "linear" } }
    : phase === "walkOff" || phase === "done" ? { x: -560, transition: { duration: 2.6, ease: "linear" } }
    : { x: 0 };
  const walking = phase === "walkIn" || phase === "walkOff" || phase === "done";
  const leaving = phase === "walkOff" || phase === "done";
  // Mallet pose around the grip: dragged on the ground behind him on the way in,
  // huge windup over the head, slam onto the pad, then flopped onto the shoulder out.
  const malletAnim =
    phase === "walkIn" ? { rotate: 152, x: -40, y: 30, transition: { duration: 0.01 } }
    : phase === "windup" ? { rotate: -142, x: 0, y: 0, transition: { duration: 0.8, ease: "easeInOut" } }
    : phase === "slam" ? { rotate: 40, x: 4, y: 4, transition: { duration: 0.16, ease: "easeIn" } }
    : leaving ? { rotate: 138, x: -34, y: 6, transition: { duration: 0.45 } }
    : { rotate: 40, x: 4, y: 4 };

  return (
    <div ref={rootRef} className="text-center py-2 relative scroll-mt-24" data-testid="highstriker-celebration">
      {/* the fairground scene */}
      <div className="relative mx-auto w-full max-w-md h-60 overflow-hidden rounded-2xl"
        style={{ background: "linear-gradient(180deg,#BFE9FF 0%,#E8F7FF 62%,#9CCB68 62%,#7FB84E 100%)" }}
        data-testid="highstriker-scene">

        {/* high-striker tower */}
        <div className="absolute" style={{ right: 26, bottom: 10 }}>
          {/* bell — keeps clanging for as long as the coins are flying */}
          <motion.div
            animate={rang ? { rotate: [0, -18, 14, -9, 5, 0], scale: [1, 1.18, 1] } : {}}
            transition={{ duration: 0.7, ease: "easeOut", repeat: 4, repeatDelay: 0 }}
            className="mx-auto"
            style={{ width: 34, height: 30 }}
            data-testid="highstriker-bell"
          >
            <svg width="34" height="30" viewBox="0 0 34 30">
              <path d="M4 24 a13 13 0 0 1 26 0z" fill="#FFC400" stroke="#B8860B" strokeWidth="2" />
              <rect x="2" y="24" width="30" height="4" rx="2" fill="#B8860B" />
              <circle cx="17" cy="6" r="3" fill="#B8860B" />
            </svg>
          </motion.div>
          {/* ding flash */}
          <AnimatePresence>
            {rang && (
              <motion.div initial={{ scale: 0.3, opacity: 1 }} animate={{ scale: 2.6, opacity: 0 }} transition={{ duration: 0.7 }}
                className="absolute rounded-full border-4 border-[#FFD600]" style={{ width: 44, height: 44, left: -5, top: -10 }} />
            )}
          </AnimatePresence>
          {rang && (
            <motion.div initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 1, scale: 1 }}
              className="absolute font-display font-extrabold text-[#B8860B]" style={{ top: -22, left: -12, fontSize: 18, whiteSpace: "nowrap" }}>
              DING!
            </motion.div>
          )}
          {/* track */}
          <div className="relative mx-auto" style={{ width: 12, height: 150, background: "#8D6E63", borderRadius: 6, border: "2px solid #5D4037" }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} className="absolute left-[2px] right-[2px]" style={{ top: 18 + i * 22, height: 3, background: "#5D4037", borderRadius: 2 }} />
            ))}
            {/* puck */}
            <motion.div
              className="absolute rounded-full"
              style={{ width: 18, height: 18, left: -3, background: "radial-gradient(circle at 35% 35%, #FF8A65, #E53935)", border: "2px solid #B71C1C" }}
              initial={{ bottom: 4 }}
              animate={rang || phase === "slam" ? (rang ? { bottom: 132 } : { bottom: 4 }) : { bottom: 4 }}
              transition={{ duration: 0.6, ease: [0.2, 0.9, 0.3, 1] }}
              data-testid="highstriker-puck"
            />
          </div>
          {/* base + strike pad (to the left, where the mallet lands) */}
          <div className="mx-auto" style={{ width: 40, height: 16, background: "#5D4037", borderRadius: 4 }} />
          <div className="absolute" style={{ left: -34, bottom: 0 }}>
            <motion.div
              animate={phase === "slam" || rang ? { scaleY: phase === "slam" ? 0.5 : 1 } : {}}
              transition={{ duration: 0.15 }}
              style={{ width: 34, height: 10, background: "#E53935", borderRadius: 4, border: "2px solid #B71C1C", transformOrigin: "bottom" }}
            />
            <div style={{ width: 42, height: 8, background: "#5D4037", borderRadius: 3, marginLeft: -4 }} />
          </div>
        </div>

        {/* the slugger + mallet (parked at the strike spot; walks in/out via x) */}
        <motion.div className="absolute" style={{ right: 92, bottom: 8, width: 92, height: 130 }}
          initial={{ x: -560 }} animate={charAnim} data-testid="highstriker-slugger">
          <motion.div
            animate={walking ? { y: [0, -4, 0], rotate: [0, 2.5, 0, -2.5, 0] } : { y: 0, rotate: 0 }}
            transition={walking ? { duration: 0.5, repeat: Infinity, ease: "easeInOut" } : { duration: 0.2 }}
            style={{ position: "relative", width: 92, height: 130 }}
          >
            <div style={{ position: "absolute", bottom: 0, left: 0, transform: leaving ? "scaleX(-1)" : "none" }}>
              <Slugger gender={gender} />
            </div>
            {/* mallet pivots around the grip near the hands */}
            <motion.div
              style={{ position: "absolute", left: 42, bottom: 44, transformOrigin: "43px 88px" }}
              animate={malletAnim}
              data-testid="highstriker-mallet"
            >
              <Mallet />
            </motion.div>
            {/* whistling notes on the way out */}
            {leaving && (
              <div className="absolute" style={{ top: -6, left: 4 }}>
                {["♪", "♫", "♪"].map((n, i) => (
                  <motion.span key={i} className="absolute font-bold text-[#0A6A2C]" style={{ fontSize: 18, left: i * 14 }}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: [0, 1, 0], y: -26 }}
                    transition={{ duration: 1.4, delay: 0.3 + i * 0.45, repeat: Infinity, repeatDelay: 0.5 }}>
                    {n}
                  </motion.span>
                ))}
              </div>
            )}
          </motion.div>
        </motion.div>
      </div>

      {/* Coin bubbles live BELOW the scene and are always mounted (opacity fade only),
          stacked vertically — the scene above never moves when they appear or count up. */}
      <div className={`flex flex-col items-center gap-1.5 mt-2 transition-opacity duration-500 ${rang ? "opacity-100" : "opacity-0"}`}>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-2xl font-extrabold font-display"
          style={{ background: "linear-gradient(135deg,#FFD600,#FF9E00)", color: "#3d2c00", boxShadow: "0 8px 24px rgba(255,158,0,0.45)" }}
          data-testid="quiz-xp-awarded">
          <span className="text-2xl">🪙</span>
          <span className="text-xl sm:text-2xl leading-none">+{count.toLocaleString()} coins today</span>
        </div>
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-2xl font-extrabold font-display"
          style={{ background: "linear-gradient(135deg,#FFD600,#FF9E00)", color: "#3d2c00", boxShadow: "0 8px 24px rgba(255,158,0,0.45)" }}
          data-testid="coin-grand-total">
          <span className="text-2xl">🪙</span>
          <span className="text-3xl leading-none text-outline-lime">{total.toLocaleString()}</span>
          <span className="text-xl leading-none">total</span>
        </div>
        {scorePercent != null && (
          <div className="text-3xl font-extrabold font-display text-[#8fb300]" data-testid="quiz-score">{scorePercent}%</div>
        )}
      </div>

      {rang && <CoinStreakBanner celebrationsEnabled={soundOn} compact className="mt-2 max-w-md mx-auto justify-center text-center" />}

      <AnimatePresence>
        {phase === "done" && (
          <motion.div ref={doneRef} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }} data-testid="coin-done" className="scroll-mb-24">
            <p className="text-black font-semibold max-w-md mx-auto mt-2" data-testid="quiz-encouragement">
              You rang the bell — keep going for even more coins!
            </p>
            <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
              {hasPrev && (
                <button data-testid="quiz-prev-button" className="btn-secondary text-sm px-4 py-2 whitespace-nowrap" onClick={() => onPrev && onPrev()}>
                  ← Prev
                </button>
              )}
              {onRetry && <button data-testid="quiz-retry-button" className="btn-secondary text-sm px-4 py-2 whitespace-nowrap" onClick={onRetry}>Redo</button>}
              {canAdvance && onContinue && (
                <button data-testid="quiz-continue-button" className="btn-primary text-sm px-4 py-2 whitespace-nowrap" onClick={onContinue}>
                  Next →
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
